import { useCallback, useState } from 'react';
import { bespokeTemplatesBulkRenderStatusBespokeTemplatesBulkRenderStatusPost } from '../../services/api/sdk.gen';
import type { BespokeTemplateResponse } from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import { countBespokeBulkTargets } from './bulkTemplateStatus';
import {
  bespokeApiErrorMessage,
  type FormSchemaVersionPin,
} from './formSchemaUtils';

export type BulkTemplateStatusDialogState = {
  direction: 'pause' | 'resume';
  scopeSummary: string;
  pinsFilter: FormSchemaVersionPin[] | null;
  affectedCount: number;
};

export function useBulkTemplateStatus({
  templates,
  onUpdated,
}: {
  templates: BespokeTemplateResponse[];
  onUpdated: () => Promise<unknown>;
}) {
  const toast = useToast();
  const [bulkBespokeDialog, setBulkBespokeDialog] =
    useState<BulkTemplateStatusDialogState | null>(null);
  const [bulkBespokeBusy, setBulkBespokeBusy] = useState(false);

  const openBulkBespokeDialog = useCallback(
    (
      direction: 'pause' | 'resume',
      scopeSummary: string,
      pinsFilter: FormSchemaVersionPin[] | null
    ) => {
      const affectedCount = countBespokeBulkTargets(
        templates,
        direction,
        pinsFilter
      );
      setBulkBespokeDialog({
        direction,
        scopeSummary,
        pinsFilter,
        affectedCount,
      });
    },
    [templates]
  );

  const closeBulkBespokeDialog = useCallback(() => {
    setBulkBespokeDialog(null);
  }, []);

  const runBulkBespokeConfirmed = useCallback(async () => {
    const d = bulkBespokeDialog;
    if (!d || d.affectedCount === 0) return;
    setBulkBespokeBusy(true);
    try {
      const res =
        await bespokeTemplatesBulkRenderStatusBespokeTemplatesBulkRenderStatusPost(
          {
            throwOnError: true,
            body: {
              direction: d.direction,
              ...(d.pinsFilter == null ? {} : { pins: d.pinsFilter }),
            },
          }
        );
      const n = res.data?.updated_count ?? 0;
      toast.success(
        'Bulk update complete',
        `${n} template${n === 1 ? '' : 's'} updated (Standby rows unchanged).`
      );
      setBulkBespokeDialog(null);
      await onUpdated();
    } catch (err) {
      console.error(err);
      toast.error('Bulk update failed', bespokeApiErrorMessage(err));
    } finally {
      setBulkBespokeBusy(false);
    }
  }, [bulkBespokeDialog, onUpdated, toast]);

  return {
    bulkBespokeDialog,
    bulkBespokeBusy,
    openBulkBespokeDialog,
    closeBulkBespokeDialog,
    runBulkBespokeConfirmed,
  };
}
